import React from "react";

// Main loading fallback for pages
export const PageLoader = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-800">
      <div className="text-center">
        <div className="relative">
          {/* Spinning loader */}
          <div className="w-16 h-16 border-4 border-[#2563EB] border-t-blue-500 rounded-full animate-spin mx-auto dark:border-gray-600 dark:border-t-blue-400"></div>

          {/* Logo in center */}
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <img
              src="/logo.png"
              alt="AkkoRent"
              className="w-8 h-8 rounded-lg object-cover"
            />
          </div>
        </div>

        <p className="mt-4 text-gray-600 font-medium">Loading...</p>
      </div>
    </div>
  );
};

// Smaller loading fallback for components
export const ComponentLoader = () => {
  return (
    <div className="flex items-center justify-center p-8">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#2563EB] border-t-blue-500 rounded-full animate-spin mx-auto dark:border-gray-600 dark:border-t-blue-400"></div>
        <p className="mt-3 text-gray-500 text-sm dark:text-gray-400">
          Loading...
        </p>
      </div>
    </div>
  );
};

// Minimal loading fallback for small components
export const MiniLoader = () => {
  return (
    <div className="flex items-center justify-center p-4">
      <div className="w-8 h-8 border-3 border-[#2563EB] border-t-blue-500 rounded-full animate-spin dark:border-gray-600 dark:border-t-blue-400"></div>
    </div>
  );
};

// Skeleton loader for product cards
export const ProductCardSkeleton = () => {
  return (
    <div className="bg-white rounded-lg shadow-md p-4 animate-pulse dark:bg-gray-700">
      <div className="bg-gray-300 h-48 rounded-md mb-4 dark:bg-gray-600"></div>
      <div className="h-4 bg-gray-300 rounded w-3/4 mb-2 dark:bg-gray-600"></div>
      <div className="h-4 bg-gray-300 rounded w-1/2 mb-4 dark:bg-gray-600"></div>
      <div className="flex justify-between">
        <div className="h-6 bg-gray-300 rounded w-1/4 dark:bg-gray-600"></div>
        <div className="h-8 bg-gray-300 rounded w-1/3 dark:bg-gray-600"></div>
      </div>
    </div>
  );
};

// Skeleton loader matching the AdRow seller listing card layout
export const AdRowSkeleton = () => {
  return (
    <div className="bg-white border border-[#D1D5DC] rounded-xl shadow-sm flex flex-col overflow-hidden animate-pulse dark:bg-gray-800 dark:border-gray-700">
      {/* Image area */}
      <div className="w-full h-[260px] bg-gray-200 dark:bg-gray-700 flex-shrink-0" />

      {/* Content */}
      <div className="px-5 pt-5 pb-5 flex flex-col flex-1 gap-3">
        {/* Category + date */}
        <div className="flex justify-between items-center">
          <div className="h-5 w-16 bg-gray-200 dark:bg-gray-700 rounded-full" />
          <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
        </div>

        {/* Title */}
        <div className="h-5 w-3/4 bg-gray-200 dark:bg-gray-700 rounded" />

        {/* Location */}
        <div className="h-4 w-1/2 bg-gray-200 dark:bg-gray-700 rounded" />

        {/* Price + View button */}
        <div className="flex items-center justify-between">
          <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded" />
          <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded-lg" />
        </div>

        {/* Divider */}
        <div className="border-t border-gray-100 dark:border-gray-700 mt-auto pt-2">
          {/* Stats row */}
          <div className="flex items-center justify-between">
            <div className="flex gap-4">
              <div className="h-4 w-16 bg-gray-200 dark:bg-gray-700 rounded" />
              <div className="h-4 w-14 bg-gray-200 dark:bg-gray-700 rounded" />
            </div>
            <div className="h-7 w-7 bg-gray-200 dark:bg-gray-700 rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
};


// Skeleton loader for navbar
export const NavbarSkeleton = () => {
  return (
    <div className="bg-white shadow-lg sticky top-0 z-50 border-b border-gray-200 dark:bg-gray-900 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-center justify-between h-16 animate-pulse">
          <div className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-gray-300 rounded-full dark:bg-gray-600"></div>
            <div className="w-24 h-6 bg-gray-300 rounded dark:bg-gray-600"></div>
          </div>
          <div className="hidden md:flex space-x-8">
            <div className="w-16 h-4 bg-gray-300 rounded dark:bg-gray-600"></div>
            <div className="w-16 h-4 bg-gray-300 rounded dark:bg-gray-600"></div>
            <div className="w-16 h-4 bg-gray-300 rounded dark:bg-gray-600"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Skeleton loader for product detail page
export const ProductDetailSkeleton = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 py-8 px-4">
      <div className="max-w-7xl mx-auto pt-4 sm:pt-8">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-700 animate-pulse">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div>
              <div className="bg-gray-200 dark:bg-gray-700 h-[300px] sm:h-[400px] lg:h-[500px]"></div>
              <div className="px-8 py-6 mb-4">
                <div className="h-14 bg-gray-200 dark:bg-gray-700 rounded-xl w-full"></div>
              </div>
            </div>
            <div className="p-8 lg:p-10">
              <div className="mb-6">
                <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-3/4 mb-4"></div>
                <div className="flex space-x-4 mb-4">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
                </div>
              </div>
              <div className="flex items-center mb-8 bg-gray-50 dark:bg-gray-700/50 p-4 rounded-xl">
                <div className="flex space-x-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                  ))}
                </div>
                <div className="ml-3 h-5 bg-gray-200 dark:bg-gray-700 rounded w-40"></div>
              </div>
              <div className="mb-8">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-3"></div>
                <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl w-full"></div>
              </div>
              <div className="mb-8">
                <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
                  ))}
                </div>
              </div>
              <div className="mb-8 pt-8 border-t border-gray-100 dark:border-gray-700">
                <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-xl w-full"></div>
              </div>
              <div className="flex justify-between items-center mt-6">
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Wishlist Skeleton Component
export const WishlistSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow border dark:border-gray-700 animate-pulse">
      <div className="flex items-center gap-4 py-4 px-4">
        {/* Image Skeleton */}
        <div className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
        
        {/* Text Skeletons */}
        <div className="flex-1">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
        </div>
        
        {/* Price Skeleton */}
        <div className="w-24">
          <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
        </div>
        
        {/* Stock Skeleton */}
        <div className="w-20">
          <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
        </div>
        
        {/* Button Skeleton */}
        <div className="w-28">
          <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-28"></div>
        </div>
        
        {/* Remove Icon Skeleton */}
        <div className="w-8">
          <div className="w-5 h-5 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

// Mobile Wishlist Skeleton
export const MobileWishlistSkeleton = () => {
  return (
    <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow border dark:border-gray-700 animate-pulse">
      <div className="w-full h-40 rounded-lg bg-gray-200 dark:bg-gray-700"></div>
      <div className="mt-3">
        <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-3"></div>
        <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
        <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-16 mb-3"></div>
        <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded-lg w-full"></div>
      </div>
    </div>
  );
};

export default PageLoader;